/**
 * @file DefineFieldsTab.js
 * @description This file contains the DefineFieldsTab component, which allows the user to define the fields of an import template.
 */

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ImportFieldDropdown from '../shared/asyncDropdowns/ImportFieldDropdown';
import {
    getImportFileFormats,
    buildImportTemplate,
} from '../../services/importService';
import style from './ImportTemplates.module.scss';
import { ImportFileFormat } from '../../utils/data/enums';
import { getText } from '../../i18n';

const DefineFieldsTab = ({
    templateData,
    setTemplateData,
    selectedTemplateType,
    setSelectedTemplateType,
}) => {
    if (!selectedTemplateType) {
        return null;
    }

    const [fileFormats, setFileFormats] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isFileUploadEnabled] = useState(false);

    useEffect(() => {
        const fetchFileFormats = async () => {
            try {
                const formats = await getImportFileFormats();
                setFileFormats(formats);
            } catch (error) {
                console.error('Error retrieving file formats:', error);
            }
        };

        fetchFileFormats();
    }, []);

    useEffect(() => {
        if (!selectedTemplateType) return;

        const currentFields = selectedTemplateType.templateFields || [];
        const updatedFields = alterTemplateFields(
            [...currentFields],
            templateData.columnPositionCount,
        );

        const currentJSON = JSON.stringify(currentFields);
        const updatedJSON = JSON.stringify(updatedFields);

        if (currentJSON !== updatedJSON) {
            setSelectedTemplateType(prevType => ({
                ...prevType,
                templateFields: updatedFields,
            }));
        }
    }, [
        selectedTemplateType?.id,
        selectedTemplateType?.processingOrder,
        templateData.columnPositionCount,
        templateData.importFileFormatId,
    ]);

    const alterTemplateFields = (templateFields = [], fieldCount) => {
        if (fieldCount <= 0) return [];

        const existingFieldCount = templateFields.length;

        if (templateData.importFileFormatId === ImportFileFormat.FixedLength) {
            // if there arent any fields for a fixed length file add 15 by default
            if (existingFieldCount === 0) {
                let addFieldCount = 1;

                while (addFieldCount <= 15) {
                    const newField = {
                        id: 0,
                        importTemplateId: templateData.id,
                        importTemplateTypeId: selectedTemplateType?.id || 0,
                        importFieldId: null,
                        startingLocation: 0,
                        length: 0,
                    };

                    templateFields.push(newField);
                    addFieldCount++;
                }
            }
        } else {
            if (existingFieldCount === fieldCount) return templateFields;

            if (existingFieldCount > fieldCount) {
                const sortedFields = [...(templateFields || [])].sort(
                    (a, b) => a.columnNumber - b.columnNumber,
                );

                return sortedFields.slice(0, fieldCount);
            }

            let addFieldCount = fieldCount - existingFieldCount;

            while (addFieldCount > 0) {
                const newField = {
                    id: 0,
                    importTemplateId: templateData.id,
                    importTemplateTypeId: selectedTemplateType?.id || 0,
                    importFieldId: null,
                    columnNumber: templateFields.length + 1,
                    startingLocation: 0,
                    length: 0,
                };

                templateFields.push(newField);
                addFieldCount--;
            }
        }
        return templateFields;
    };

    const handleInputChange = (event, index) => {
        const { name, value } = event.target;
        const updatedFields = [...(selectedTemplateType?.templateFields || [])];
        updatedFields[index] = {
            ...updatedFields[index],
            [name]:
                name === 'startingLocation' || name === 'length'
                    ? parseInt(value, 10)
                    : value,
        };

        setSelectedTemplateType(prevType => ({
            ...prevType,
            templateFields: updatedFields,
        }));
    };

    const handleLengthChange = (event, index) => {
        const { value } = event.target;

        if (!value || isNaN(value)) return;

        const updatedFields = [...(selectedTemplateType?.templateFields || [])];

        const {
            length: originalLength,
            startingLocation: originalStartingLoction,
        } = updatedFields[index];

        var currentField = {
            ...updatedFields[index],
            length: parseInt(value, 10),
        };

        updatedFields[index] = currentField;

        // if it isnt the last field
        if (index + 1 < updatedFields.length) {
            let nextField = updatedFields[index + 1];

            // if the next field doesn't have a starting location specified
            // or the starting location is right after the field having the length updated
            if (
                nextField.startingLocation === 0 ||
                nextField.startingLocation ===
                    originalStartingLoction + originalLength
            ) {
                nextField.startingLocation =
                    currentField.startingLocation + currentField.length;
                updatedFields[index + 1] = nextField;
            }
        }

        setSelectedTemplateType(prevType => ({
            ...prevType,
            templateFields: updatedFields,
        }));
    };

    const handleAddRow = () => {
        const newField = {
            id: 0,
            importTemplateId: templateData.id,
            importTemplateTypeId: selectedTemplateType?.id || 0,
            importFieldId: null,
            columnNumber:
                (selectedTemplateType?.templateFields || []).length + 1,
            startingLocation: 0,
            length: 0,
        };

        setSelectedTemplateType(prevType => ({
            ...prevType,
            templateFields: [...(prevType.templateFields || []), newField],
        }));
    };

    const handleDeleteRow = index => {
        const updatedFields = [...(selectedTemplateType?.templateFields || [])];
        updatedFields.splice(index, 1);

        setSelectedTemplateType(prevType => ({
            ...prevType,
            templateFields: updatedFields.map((field, i) => ({
                ...field,
                columnNumber: i + 1,
            })),
        }));
    };

    const handleMoveRowUp = index => {
        if (index > 0) {
            const updatedFields = [
                ...(selectedTemplateType?.templateFields || []),
            ];
            const temp = updatedFields[index];
            updatedFields[index] = updatedFields[index - 1];
            updatedFields[index - 1] = temp;

            setSelectedTemplateType(prevType => ({
                ...prevType,
                templateFields: updatedFields.map((field, i) => ({
                    ...field,
                    columnNumber: i + 1,
                })),
            }));
        }
    };

    const handleMoveRowDown = index => {
        if (index < (selectedTemplateType?.templateFields || []).length - 1) {
            const updatedFields = [
                ...(selectedTemplateType?.templateFields || []),
            ];
            const temp = updatedFields[index];
            updatedFields[index] = updatedFields[index + 1];
            updatedFields[index + 1] = temp;

            setSelectedTemplateType(prevType => ({
                ...prevType,
                templateFields: updatedFields.map((field, i) => ({
                    ...field,
                    columnNumber: i + 1,
                })),
            }));
        }
    };

    const handleFileChange = event => {
        setSelectedFile(event.target.files[0]);
    };

    const handleUploadFile = async () => {
        if (!selectedFile) return;

        try {
            const builtTemplate = await buildImportTemplate(
                selectedTemplateType?.importTypeId,
                templateData.importFileFormatId,
                templateData.importDelimiterId,
                templateData.customDelimiter,
                templateData.sheetName,
                selectedFile,
            );

            const mappedTemplateFields = builtTemplate.map(field => ({
                id: 0,
                importTemplateId: templateData.id,
                importFieldId: field.importFieldId,
                columnNumber: field.columnNumber,
                headerName: field.header,
                startingLocation: 0,
                length: 0,
            }));

            setSelectedTemplateType(prevType => ({
                ...prevType,
                templateFields: mappedTemplateFields,
            }));
        } catch (error) {
            console.error('Error building import template:', error);
        }
    };

    const isFixedLengthFile =
        templateData.importFileFormatId === ImportFileFormat.FixedLength;
    const isExcelFile =
        templateData.importFileFormatId === ImportFileFormat.Excel;

    return (
        <div className={`${style.defineFieldsContainer} mt-5`}>
            {isFileUploadEnabled && (
                <div className="row mb-3">
                    <div className="col-sm-12">
                        <div className="input-group">
                            <input
                                type="file"
                                className="form-control"
                                onChange={handleFileChange}
                            />
                            <button
                                className="btn btn-primary"
                                onClick={handleUploadFile}
                                disabled={!selectedFile}
                            >
                                {getText('msg_dcf4cc69bca4')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <div className="col-12 col-xs-12 col-md-10 col-lg-10 m-auto">
                <div className="d-flex flex-wrap mb-3 column-gap-2">
                    {!isFixedLengthFile && (
                        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-1">
                            {getText('msg_65ba00e95e60')}
                        </div>
                    )}
                    {isFileUploadEnabled && (
                        <div className="col-md-3">
                            {getText('msg_37d8ce784d80')}
                        </div>
                    )}
                    {isFixedLengthFile && (
                        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-1 p">
                            {getText('msg_02f34a5f7737')}
                        </div>
                    )}
                    {isFixedLengthFile && (
                        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-1">
                            {getText('msg_3bade34ecbc5')}
                        </div>
                    )}
                    <div className="col-xs-12 col-sm-12 col-md-12 col-lg-4 flex-fill">
                        {getText('msg_d42713493ca8')}
                    </div>
                    <div className="col-xs-12 col-sm-12 col-md-2 col-lg-3 d-flex">
                        {getText('msg_c3cd636a585b')}
                    </div>
                </div>
                {(selectedTemplateType?.templateFields || []).map(
                    (field, index) => (
                        <div
                            className="d-flex flex-wrap mb-3 column-gap-2"
                            key={`${field.id}-${index}`}
                        >
                            {!isFixedLengthFile && (
                                <div className="col-xs-12 col-sm-12 col-md-12 col-lg-1 my-auto pb-3">
                                    {isExcelFile
                                        ? String.fromCharCode(65 + index)
                                        : field.columnNumber}
                                </div>
                            )}
                            {isFileUploadEnabled && (
                                <div className="col-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={field.headerName || ''}
                                        onChange={event =>
                                            handleInputChange(event, index)
                                        }
                                        name="headerName"
                                    />
                                </div>
                            )}
                            {isFixedLengthFile && (
                                <div className="col-xs-12 col-sm-12 col-md-12 col-lg-1">
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={field.startingLocation || 0}
                                        onChange={event =>
                                            handleInputChange(event, index)
                                        }
                                        name="startingLocation"
                                    />
                                </div>
                            )}
                            {isFixedLengthFile && (
                                <div className="col-xs-12 col-sm-12 col-md-12 col-lg-1">
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={field.length || 0}
                                        onChange={event =>
                                            handleLengthChange(event, index)
                                        }
                                        name="length"
                                    />
                                </div>
                            )}
                            <div className="col-xs-12 col-sm-12 col-md-12 col-lg-4 flex-fill">
                                <ImportFieldDropdown
                                    importTypeId={
                                        selectedTemplateType?.importTypeId
                                    }
                                    selectedField={
                                        field.importFieldId
                                            ? {
                                                  id: field.importFieldId,
                                                  name: field.importFieldName,
                                              }
                                            : null
                                    }
                                    onFieldChange={selectedField => {
                                        const updatedFields = [
                                            ...(selectedTemplateType?.templateFields ||
                                                []),
                                        ];
                                        updatedFields[index] = {
                                            ...updatedFields[index],
                                            importFieldId:
                                                selectedField?.id || null,
                                            importFieldName:
                                                selectedField?.name || '',
                                        };

                                        setSelectedTemplateType(prevType => ({
                                            ...prevType,
                                            templateFields: updatedFields,
                                        }));
                                    }}
                                    required
                                />
                            </div>
                            <div className="col-xs-12 col-sm-12 col-md-2 col-lg-3 d-flex">
                                <i
                                    className="bi bi-trash3 me-2 text-danger mx-1 mb-3 align-content-center "
                                    onClick={() => handleDeleteRow(index)}
                                ></i>

                                <button
                                    className="btn btn-secondary m-auto mx-1 mb-3 align-content-center"
                                    onClick={() => handleMoveRowUp(index)}
                                >
                                    {getText('msg_2038bdec9210')}
                                </button>
                                <button
                                    className="btn btn-secondary m-auto mx-1 mb-3 align-content-center"
                                    onClick={() => handleMoveRowDown(index)}
                                >
                                    {getText('msg_bf93e5ce8bc1')}
                                </button>
                            </div>
                        </div>
                    ),
                )}
                {isFixedLengthFile && (
                    <div className="text-center align-items-center me-4 d-lg-block d-sm-none">
                        <div className="mb-3 mt-5">
                            <div className="col-sm-12 d-flex">
                                <button
                                    type="button"
                                    onClick={() => handleAddRow()}
                                    className="ing-popup-blue-negative submit-button-form"
                                >
                                    {getText('msg_680a2682259e')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

DefineFieldsTab.propTypes = {
    templateData: PropTypes.object.isRequired,
    setTemplateData: PropTypes.func.isRequired,
    selectedTemplateType: PropTypes.object,
    setSelectedTemplateType: PropTypes.func.isRequired,
};

export default DefineFieldsTab;
